import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { type License, useGetLicenses } from "@src/appV2/Licenses/api/useGetLicenses";
import { useAutoApprovedQualifications } from "@src/appV2/Licenses/useAutoApprovedQualifications";
import { useGetQualifications } from "@src/appV2/Qualifications/api/useGetQualifications";
import { addMonths, isBefore, parseISO } from "date-fns";

import { type WorkerRequirement } from "../../Documents/api/types";
import { useIsJobInterviewsEnabled } from "../../Placements/useIsJobInterviewsEnabled";
import { useGetWorkerOpenPlacementFilters } from "../api/useGetWorkerOpenPlacementFilters";

export const OnboardingRequirementStatuses = {
  COMPLETED: "COMPLETED",
  IN_PROGRESS: "IN_PROGRESS",
  EXPIRED: "EXPIRED",
  EXPIRING: "EXPIRING",
  MISSING: "MISSING",
  REJECTED: "REJECTED",
} as const;

export type OnboardingRequirementStatus =
  (typeof OnboardingRequirementStatuses)[keyof typeof OnboardingRequirementStatuses];

export interface OnboardingRequirement {
  name: string;
  stepLabel: string;
  stepAction: string;
  status: OnboardingRequirementStatus;
  expiresAt?: string;
  subItems: string[];
  getDisplayName: () => string;
}

function getLicenseRequirement(licenses: License[]): {
  licenses: string[];
  status: OnboardingRequirementStatus;
  expiresAt?: string;
} {
  if (licenses.length === 0) {
    return { status: OnboardingRequirementStatuses.MISSING, expiresAt: undefined, licenses: [] };
  }

  const statusCount = licenses.reduce<
    Record<LicenseStatuses, { count: number; licenses: string[] }>
  >(
    (statusCount, license) => {
      if (
        (license.status === LicenseStatuses.ACTIVE &&
          isDefined(license.expiresAt) &&
          isBefore(parseISO(license.expiresAt), addMonths(new Date(), 1))) ||
        license.status === LicenseStatuses.EXPIRING
      ) {
        return {
          ...statusCount,
          [LicenseStatuses.EXPIRING]: {
            count: statusCount[LicenseStatuses.EXPIRING].count + 1,
            licenses: [...statusCount[license.status].licenses, license.qualification],
          },
        };
      }

      return {
        ...statusCount,
        [license.status]: {
          count: statusCount[license.status].count + 1,
          licenses: [...statusCount[license.status].licenses, license.qualification],
        },
      };
    },
    {
      [LicenseStatuses.ACTIVE]: { count: 0, licenses: [] },
      [LicenseStatuses.ARCHIVED]: { count: 0, licenses: [] },
      [LicenseStatuses.EXPIRED]: { count: 0, licenses: [] },
      [LicenseStatuses.EXPIRING]: { count: 0, licenses: [] },
      [LicenseStatuses.PENDING]: { count: 0, licenses: [] },
      [LicenseStatuses.REJECTED]: { count: 0, licenses: [] },
    }
  );

  if (statusCount[LicenseStatuses.ACTIVE].count > 0) {
    return {
      status: OnboardingRequirementStatuses.COMPLETED,
      licenses: statusCount[LicenseStatuses.ACTIVE].licenses,
    };
  }

  if (statusCount[LicenseStatuses.REJECTED].count > 0) {
    return {
      status: OnboardingRequirementStatuses.REJECTED,
      licenses: statusCount[LicenseStatuses.REJECTED].licenses,
    };
  }

  if (statusCount[LicenseStatuses.EXPIRED].count > 0) {
    const expiredLicense = licenses.find(
      (license) => license.expiresAt && isBefore(parseISO(license.expiresAt), new Date())
    );
    return {
      status: OnboardingRequirementStatuses.EXPIRED,
      expiresAt: expiredLicense?.expiresAt ?? undefined,
      licenses: statusCount[LicenseStatuses.EXPIRED].licenses,
    };
  }

  if (statusCount[LicenseStatuses.EXPIRING].count > 0) {
    const expiringLicense = licenses.find(
      (license) =>
        license.expiresAt && isBefore(parseISO(license.expiresAt), addMonths(new Date(), 1))
    );
    return {
      status: OnboardingRequirementStatuses.EXPIRING,
      expiresAt: expiringLicense?.expiresAt ?? undefined,
      licenses: statusCount[LicenseStatuses.EXPIRING].licenses,
    };
  }

  return {
    status: OnboardingRequirementStatuses.IN_PROGRESS,
    licenses: statusCount[LicenseStatuses.PENDING].licenses,
  };
}

export function getDocumentRequirement(
  workerRequirementData: WorkerRequirement[],
  requirementId: string
): { status: OnboardingRequirementStatus; expiresAt?: string } {
  const requirement = workerRequirementData.find(
    (requirement) => requirement.requirementId === requirementId
  );

  if (!requirement) {
    return { status: OnboardingRequirementStatuses.MISSING };
  }

  if (requirement.status === WorkerRequirementStatus.APPROVED) {
    return { status: OnboardingRequirementStatuses.COMPLETED };
  }

  if (requirement.status === WorkerRequirementStatus.EXPIRING) {
    return { status: OnboardingRequirementStatuses.EXPIRING, expiresAt: requirement.expiry };
  }

  if (requirement.status === WorkerRequirementStatus.EXPIRED) {
    return { status: OnboardingRequirementStatuses.EXPIRED, expiresAt: requirement.expiry };
  }

  if (requirement.status === WorkerRequirementStatus.REJECTED) {
    return { status: OnboardingRequirementStatuses.REJECTED };
  }

  if (requirement.status === WorkerRequirementStatus.IN_REVIEW) {
    return { status: OnboardingRequirementStatuses.IN_PROGRESS };
  }

  return { status: OnboardingRequirementStatuses.MISSING };
}

export function useOnboardingRequirement(workerId: string) {
  const { data: allWorkerLicenses, isLoading: isLicensesLoading } = useGetLicenses({ workerId });
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();

  const { data: allQualificationsData, isLoading: isAllQualificationsLoading } =
    useGetQualifications({
      enabled: isJobInterviewsEnabled,
    });

  const { data: availableWorkerTypes } = useGetWorkerOpenPlacementFilters({
    workerId,
    useHiringWorkerTypes: true,
  });

  const autoApprovedQualifications = useAutoApprovedQualifications();

  let licenses: License[] = [];

  // Get licenses from existing worker licenses if available
  if (isDefined(allWorkerLicenses) && isDefined(availableWorkerTypes)) {
    licenses = allWorkerLicenses.filter((license) =>
      availableWorkerTypes.includes(license.qualification)
    );
  }

  // If no licenses found, fall back to auto-approved qualifications
  if (
    licenses.length === 0 &&
    isDefined(autoApprovedQualifications) &&
    isDefined(allQualificationsData) &&
    isDefined(availableWorkerTypes)
  ) {
    licenses =
      allQualificationsData.data
        ?.filter((qualification) => {
          const isAutoApproved = autoApprovedQualifications.has(qualification.attributes.name);
          const isInAvailableTypes = availableWorkerTypes.includes(qualification.attributes.name);
          return isAutoApproved && isInAvailableTypes;
        })
        .map((qualification) => ({
          // this is an auto-approved license
          // so we need to create a new license object
          // these default values are legitimate, and are not being used for any action
          id: qualification.id,
          workerUserId: workerId,
          qualification: qualification.attributes.name,
          state: "Any",
          multiState: true,
          number: null,
          expiresAt: null,
          status: LicenseStatuses.ACTIVE,
          createdAt: new Date().toISOString(),
          updatedByType: null,
          rejectedAt: null,
          rejectionReason: null,
          notifyRejection: null,
          rejectionNoteForWorker: null,
          reviewedAt: null,
        })) ?? [];
  }

  const licenseRequirement = getLicenseRequirement(licenses ?? []);

  const requirement: OnboardingRequirement = {
    name: "license",
    stepLabel: "Upload your license",
    stepAction: `Add an applicable license to start ${
      isJobInterviewsEnabled ? "booking interviews" : "applying to jobs"
    }`,
    status: licenseRequirement.status,
    expiresAt: licenseRequirement.expiresAt,
    subItems: licenseRequirement.licenses,
    getDisplayName() {
      if (licenseRequirement.licenses.length > 1) {
        return "license";
      }

      if (licenseRequirement.licenses.length === 1) {
        return `${licenseRequirement.licenses[0]} license`;
      }

      return "license";
    },
  };

  return {
    requirement,
    isLoading: isLicensesLoading || isAllQualificationsLoading,
    isInvalid:
      licenseRequirement.status === OnboardingRequirementStatuses.REJECTED ||
      licenseRequirement.status === OnboardingRequirementStatuses.EXPIRED,
    isPending: licenseRequirement.status === OnboardingRequirementStatuses.IN_PROGRESS,
    isExpiring: licenseRequirement.status === OnboardingRequirementStatuses.EXPIRING,
    isCompleted:
      licenseRequirement.status === OnboardingRequirementStatuses.COMPLETED ||
      licenseRequirement.status === OnboardingRequirementStatuses.EXPIRING,
  };
}
